import React, { useState } from 'react';
import { Heart, Users, Brain, Home, Shield, Sparkles, ArrowRight, Construction, Calendar } from 'lucide-react';
import { Button } from './ui/button';

export const GiveMore = () => {
  const [showUnderConstruction] = useState(true); // Under construction mode

  const causes = [
    {
      id: 1,
      icon: Sparkles,
      title: "Childhood Debilitating Illnesses",
      description: "Supporting children facing life-altering health challenges with resources, hope, and advocacy.",
      impact: "Every child deserves a fighting chance"
    },
    {
      id: 2,
      icon: Heart,
      title: "Cancer Support",
      description: "Standing with warriors in their battle against cancer through financial aid and emotional support.",
      impact: "Because the fight isn't over until it's won"
    },
    {
      id: 3,
      icon: Brain,
      title: "Mental Health Advocacy",
      description: "Breaking the stigma and providing resources for those battling invisible wars within their minds.",
      impact: "Your mind matters, your life matters"
    },
    {
      id: 4,
      icon: Home,
      title: "Homelessness Initiatives",
      description: "Creating pathways from streets to stability through shelter, resources, and dignity restoration.",
      impact: "Everyone deserves a place to call home"
    },
    {
      id: 5,
      icon: Shield,
      title: "Addiction Recovery",
      description: "Supporting those reclaiming their lives from addiction with treatment access and community.",
      impact: "Recovery is possible, redemption is real"
    },
    {
      id: 6,
      icon: Users,
      title: "Domestic Violence Advocacy",
      description: "Protecting survivors and providing safe havens for those escaping dangerous situations.",
      impact: "Breaking cycles, rebuilding lives"
    }
  ];

  return (
    <section id="give-more" className="relative py-24 bg-gunmetal-900">
      {/* UNDER CONSTRUCTION BANNER */}
      {showUnderConstruction && (
        <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-r from-emerald-500/20 via-emerald-600/30 to-emerald-500/20 border-b border-emerald-500/50 py-4 px-6">
          <div className="max-w-4xl mx-auto flex items-center justify-center space-x-4">
            <Construction className="text-emerald-400 animate-pulse" size={28} />
            <div className="text-center">
              <p className="text-emerald-200 font-bold text-lg">GIVE MORE DONATION STATION - COMING SOON!</p>
              <p className="text-emerald-300/80 text-sm flex items-center justify-center">
                <Calendar size={14} className="mr-1" />
                Official Launch: <span className="font-bold ml-1">July 17, 2026</span>
              </p>
            </div>
            <Construction className="text-emerald-400 animate-pulse" size={28} />
          </div>
        </div>
      )}

      {/* Background */}
      <div className="absolute inset-0 opacity-10">
        <img 
          src="https://images.unsplash.com/photo-1697665387559-253e7a645e96"
          alt="Helping Hands"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gunmetal-900 via-gunmetal-900/95 to-gunmetal-900"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
            <span className="text-platinum animate-chrome-pulse block mb-4">GIVE MORE</span>
            <span className="text-accent-red text-3xl md:text-4xl">DONATION STATION</span>
          </h2>
          <div className="w-24 h-1 bg-accent-red mx-auto mb-8"></div>
          <p className="text-2xl md:text-3xl text-steel-200 max-w-4xl mx-auto leading-relaxed font-bold mb-6">
            Join Us in the Fight for Social Restoration
          </p>
          <p className="text-xl text-steel-400 max-w-3xl mx-auto mb-4">
            At MINDESIGN ENTERPRISES, we live up to our motto. We dedicate <span className="text-accent-red font-bold text-2xl">10%</span> of all merchandise sales to our core causes.
          </p>
          <p className="text-lg text-steel-300 italic">
            "All things have room to grow" — Including hope, healing, and humanity.
          </p>
        </div>

        {/* 10% Commitment Banner */}
        <div className="bg-gradient-to-r from-accent-red to-accent-red-dark rounded-lg p-8 md:p-12 mb-16 text-center shadow-2xl shadow-accent-red/30">
          <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-8">
            <div className="text-white">
              <div className="text-6xl md:text-8xl font-black mb-2">10%</div>
              <p className="text-xl md:text-2xl font-bold">Of Every Sale</p>
            </div>
            <div className="hidden md:block w-1 h-24 bg-white/30"></div>
            <div className="text-white max-w-xl">
              <p className="text-lg md:text-xl leading-relaxed">
                Every book you buy, every song you stream, every piece of art you purchase—<strong>10% goes directly to changing lives.</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Causes Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {causes.map((cause, index) => {
            const IconComponent = cause.icon;
            return (
              <div 
                key={cause.id}
                className="group bg-gradient-to-br from-gunmetal-800/70 to-gunmetal-950/70 backdrop-blur-sm rounded-lg p-8 border-2 border-steel-700/30 hover:border-accent-red transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-accent-red/20"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Icon */}
                <div className="mb-6">
                  <div className="w-16 h-16 bg-accent-red/10 rounded-lg flex items-center justify-center group-hover:bg-accent-red/20 transition-colors duration-300">
                    <IconComponent className="text-accent-red" size={32} />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-steel-100 mb-3 group-hover:text-accent-red transition-colors duration-300">
                  {cause.title}
                </h3>
                <p className="text-steel-400 leading-relaxed mb-4">
                  {cause.description}
                </p>

                {/* Impact Statement */}
                <div className="pt-4 border-t border-steel-700/30">
                  <p className="text-accent-red font-semibold italic text-sm">
                    "{cause.impact}"
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Impact Statement Section */}
        <div className="bg-gunmetal-950/90 backdrop-blur-sm rounded-lg p-10 md:p-16 mb-16 border-l-4 border-accent-red relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-accent-red/5 to-transparent"></div>
          
          <div className="relative z-10">
            <h3 className="text-3xl md:text-4xl font-black text-steel-100 mb-6">
              Why We <span className="text-accent-red">GIVE MORE</span>
            </h3>
            <div className="space-y-4 text-steel-300 text-lg leading-relaxed">
              <p>
                The <strong className="text-accent-red">GIVE MORE</strong> pillar isn't just words on a page—it's a commitment to action. 
                We've walked through darkness. We've hit rock bottom. We've clawed our way back.
              </p>
              <p>
                Now? We reach back and pull others up with us.
              </p>
              <p className="text-xl font-bold text-steel-200">
                Because giving more isn't about what you can spare—it's about what you can share.
              </p>
              <p>
                Your time. Your resources. Your story. Your strength. When you rise, you help others rise too. 
                That's the MINDESIGN way.
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action Section */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Support Through Purchase */}
          <div className="bg-gradient-to-br from-gunmetal-800 to-gunmetal-900 rounded-lg p-8 border-2 border-steel-700/30">
            <h4 className="text-2xl font-bold text-steel-100 mb-4">
              Support Through Your Purchase
            </h4>
            <p className="text-steel-400 mb-6 leading-relaxed">
              Every MINDESIGN product you buy automatically contributes to these causes. 
              Shop with purpose. Create impact. Change lives.
            </p>
            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-accent-red rounded-full"></div>
                <span className="text-steel-300">Buy a book → Feed a family</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-accent-red rounded-full"></div>
                <span className="text-steel-300">Stream music → Fund treatment</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-accent-red rounded-full"></div>
                <span className="text-steel-300">Share our content → Spread hope</span>
              </div>
            </div>
            <Button 
              className="w-full bg-accent-red hover:bg-accent-red-dark text-white font-bold py-4 text-lg group"
              onClick={() => window.location.hash = '#books'}
            >
              Shop With Purpose
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={20} />
            </Button>
          </div>

          {/* Direct Donation */}
          <div className="bg-gradient-to-br from-accent-red/20 to-accent-red/5 rounded-lg p-8 border-2 border-accent-red/50">
            <h4 className="text-2xl font-bold text-steel-100 mb-4">
              Make a Direct Impact
            </h4>
            <p className="text-steel-400 mb-6 leading-relaxed">
              Want to give more right now? Every dollar counts. Every contribution matters. 
              100% of direct donations go to our partner organizations.
            </p>
            <div className="bg-gunmetal-900/50 rounded-lg p-6 mb-6">
              <p className="text-steel-300 text-sm italic mb-4">
                "We believe all things have room to grow—including the capacity for compassion in our communities."
              </p>
              <p className="text-steel-500 text-xs">
                — Jose Francisco Rodriguez Jr., Founder
              </p>
            </div>
            <Button 
              className="w-full border-2 border-accent-red text-accent-red hover:bg-accent-red hover:text-white font-bold py-4 text-lg transition-all duration-300 group"
              onClick={() => alert('Donation portal coming soon! For now, 10% of all merchandise sales support these causes.')}
            >
              Donate Now
              <Heart className="ml-2 group-hover:scale-110 transition-transform duration-300" size={20} />
            </Button>
          </div>
        </div>

        {/* Bottom Quote */}
        <div className="mt-16 text-center">
          <blockquote className="text-3xl md:text-4xl font-black text-diamond mb-4">
            LIVE MORE. LOVE MORE. LAUGH MORE. GIVE MORE.
          </blockquote>
          <p className="text-steel-400 text-lg">
            This is how we redesign the world—one act of generosity at a time.
          </p>
        </div>
      </div>
    </section>
  );
};
